// Modal shown to the restaurant right after marking an item as غير متوفر.
// Lists menu items in the same category that are still available and lets
// the owner push up-to-4 replacement suggestions to the customer — surfaced
// on the customer's Tracking page and mirrored into the order chat (if a
// driver is already assigned).
import { useEffect, useMemo, useState } from "react";
import api, { extractError } from "@/api/client";
import { X, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function SuggestAlternativeModal({ order, itemIndex, itemName, onClose }) {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState({}); // {menu_item_id: true}
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const unavailableItem = order?.items?.[itemIndex];
  const category = unavailableItem?.category || "";

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/restaurant/menu")
      .then(({ data }) => { if (alive) setMenu(data || []); })
      .catch((e) => { if (alive) setErr(extractError(e) || "تعذّر تحميل القائمة"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Rank suggestions: same-category first, otherwise available items
  // sorted by price proximity to the unavailable item.
  const suggestions = useMemo(() => {
    const target = unavailableItem?.unit_price ?? unavailableItem?.line_total ?? 0;
    const others = (menu || []).filter((m) =>
      m.available !== false && (m.name || "").trim() !== (itemName || "").trim()
    );
    const sameCat = category ? others.filter((m) => (m.category || "") === category) : [];
    const fallback = category ? others.filter((m) => (m.category || "") !== category) : others;
    const byPrice = (arr) => arr.slice().sort(
      (a, b) => Math.abs((a.price || 0) - target) - Math.abs((b.price || 0) - target)
    );
    return [...byPrice(sameCat), ...byPrice(fallback)].slice(0, 12);
  }, [menu, category, itemName, unavailableItem]);

  const toggle = (id) => setPicked((p) => {
    const next = { ...p };
    if (next[id]) delete next[id]; else next[id] = true;
    // Cap at 4 suggestions to keep the message readable.
    const keys = Object.keys(next);
    if (keys.length > 4) delete next[keys[0]];
    return next;
  });

  const pickedList = useMemo(() =>
    suggestions.filter((m) => picked[m.id]).map((m) => ({
      menu_item_id: m.id, name: m.name, price: Number(m.price || 0),
    })), [picked, suggestions]);

  const submit = async () => {
    if (pickedList.length === 0) { setErr("اختر بديلاً واحداً على الأقل"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/restaurant/orders/${order.id}/suggest-alternatives`, {
        unavailable_item_index: itemIndex,
        unavailable_item_name: itemName,
        suggestions: pickedList,
        note: note.trim() || null,
      });
      toast.success(`تم إرسال ${pickedList.length} اقتراح للزبون`, { duration: 3000 });
      onClose?.();
    } catch (e) { setErr(extractError(e) || "تعذّر إرسال الاقتراح"); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="suggest-alternative-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col talabya-shadow-hover">
        <div className="p-5 border-b border-[var(--border)] flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-[11px] font-black text-emerald-700 mb-1">
              <Sparkles size={14}/> اقترح بديلاً للزبون
            </div>
            <div className="font-black text-base" style={{ fontFamily: "Cairo" }}>
              الصنف <span className="text-red-600">"{itemName}"</span> غير متوفر
            </div>
            <div className="text-xs text-[var(--text-secondary)] mt-1">
              اختر حتى 4 بدائل تناسب الزبون — سنُرسلها له في التتبع والمحادثة.
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center"
            data-testid="suggest-modal-close" aria-label="إغلاق"
          >
            <X size={16}/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading && <div className="text-center py-6 text-sm text-[var(--text-secondary)]">جاري تحميل القائمة…</div>}
          {!loading && suggestions.length === 0 && (
            <div className="text-center py-6 text-sm text-[var(--text-secondary)]">
              لا توجد أصناف متاحة لاقتراحها.
            </div>
          )}
          {!loading && suggestions.length > 0 && (
            <div className="grid grid-cols-1 gap-2" data-testid="suggest-list">
              {suggestions.map((m) => {
                const isPicked = !!picked[m.id];
                return (
                  <button
                    key={m.id}
                    onClick={() => toggle(m.id)}
                    className={`text-right border-2 rounded-2xl p-3 flex items-center justify-between gap-3 transition ${
                      isPicked
                        ? "border-emerald-500 bg-emerald-50"
                        : "border-[var(--border)] bg-white hover:border-emerald-300"
                    }`}
                    data-testid={`suggest-option-${m.id}`}
                    aria-pressed={isPicked}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-sm truncate" style={{ fontFamily: "Cairo" }}>
                        {m.name}
                      </div>
                      <div className="text-[11px] text-[var(--text-secondary)] mt-0.5 truncate">
                        {m.category || "—"}
                      </div>
                    </div>
                    <div className="shrink-0 font-black text-primary-brand text-sm">
                      {Number(m.price || 0).toFixed(2)} د.ل
                    </div>
                    <div className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black transition ${
                      isPicked ? "bg-emerald-500 text-white" : "bg-gray-100 text-gray-400"
                    }`}>
                      {isPicked ? "✓" : ""}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[var(--border)] bg-gray-50 space-y-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="ملاحظة اختيارية للزبون…"
            maxLength={180}
            className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-white focus:border-emerald-400 focus:outline-none text-sm"
            data-testid="suggest-note-input"
          />
          {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
          <div className="flex gap-2">
            <button
              onClick={submit}
              disabled={busy || pickedList.length === 0}
              className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-black hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              data-testid="suggest-send-btn"
            >
              <Send size={14}/> إرسال {pickedList.length > 0 ? `(${pickedList.length})` : ""}
            </button>
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl bg-white border border-[var(--border)] text-sm font-bold hover:bg-gray-100"
              data-testid="suggest-skip-btn"
            >
              تخطّي
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
